import {
  Avatar,
  CircularProgress,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { orange } from '@mui/material/colors';
import DeleteIcon from '@mui/icons-material/Delete';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import { Skeleton } from '@mui/material';
import React, { useContext } from 'react';
import useToggle from '../../../../../../hooks/useToggle';
import {
  GetCoreUserFormattedName,
  ICoreUser,
} from '../../../../../../shared-interfaces/User/ICoreUser';
import RemoveConnectionDialog from './RemoveConnectionDialog';
import UserContext from '../../../../../../shared-components/contexts/UserContext';

interface IConnectionListItem {
  user: ICoreUser;
  selectedUser: ICoreUser;
  isNew?: boolean;
  connectionType: 'advisor' | 'advisee';
  onRemove: (student: ICoreUser, advisor: ICoreUser) => void;
}

const useStyles = makeStyles({
  connectionName: {
    fontWeight: 600,
  },
});

export const ConnectionListItemSkeletonLoader = () => {
  return (
    <>
      <ListItem style={{ borderBottom: '1px solid #dcdcdc' }}>
        <ListItemAvatar>
          <Skeleton variant="circular" height={48} width={48} />
        </ListItemAvatar>
        <ListItemText
          primary={<Skeleton variant="text" width={250} height={30} />}
          secondary={<Skeleton variant="text" width={100} height={20} />}
        />
        <ListItemSecondaryAction>
          <Skeleton variant="rectangular" width={24} height={24} />
        </ListItemSecondaryAction>
      </ListItem>
    </>
  );
};

const ConnectionListItem = (props: IConnectionListItem) => {
  const classes = useStyles();
  const _user = useContext(UserContext);

  const [openRemoveConnectionDialog, toggleOpenRemoveConnectionDialog] =
    useToggle();

  return (
    <>
      <ListItem style={{ borderBottom: '1px solid #dcdcdc' }}>
        <ListItemAvatar>
          <Avatar
            src={
              props.user.profileImageFilePath ||
              props.user.institutionProfileImageFilePath
            }
            alt={`image for ${GetCoreUserFormattedName(props.user, false)}`}
          />
        </ListItemAvatar>
        <ListItemText
          disableTypography
          primary={
            <>
              <Typography variant="h3" className={classes.connectionName}>
                {GetCoreUserFormattedName(props.user, false)}
                {props.isNew && <FiberNewIcon style={{ color: orange[700] }} />}
              </Typography>
            </>
          }
          secondary={
            <Typography variant="subtitle2">
              ID: {props.user.externalId}
            </Typography>
          }
        />
        <ListItemSecondaryAction>
          {props.isNew ? (
            <CircularProgress size={24} style={{ marginRight: '12px' }} />
          ) : (
            <>
              {_user.isAdministrator && (
                <IconButton
                  color="primary"
                  onClick={() => toggleOpenRemoveConnectionDialog()}
                  size="large"
                >
                  <DeleteIcon />
                  <span className="sr-only">Remove Connection</span>
                </IconButton>
              )}
            </>
          )}
        </ListItemSecondaryAction>
      </ListItem>
      <RemoveConnectionDialog
        open={openRemoveConnectionDialog}
        onClose={toggleOpenRemoveConnectionDialog}
        student={
          props.connectionType === 'advisee' ? props.user : props.selectedUser
        }
        advisor={
          props.connectionType === 'advisor' ? props.user : props.selectedUser
        }
        onRemove={props.onRemove}
      />
    </>
  );
};

export default ConnectionListItem;
